type GeolocationWatchEntry = {
  geolocationProvider: Geolocation;
  watchId: number;
};

const activeWatches = new Set<GeolocationWatchEntry>();

/**
 * Tracks an active `watchPosition` id so all watches can be cleared on page teardown.
 */
export function registerGeolocationWatch(
  geolocationProvider: Geolocation,
  watchId: number,
): void {
  if (watchId === 0) {
    return;
  }

  activeWatches.add({ geolocationProvider, watchId });
}

/**
 * Attempts to stop a geolocation watch. Returns false when `clearWatch` throws
 * (common during WKWebView teardown).
 */
export function clearGeolocationWatch(
  geolocationProvider: Geolocation,
  watchId: number,
): boolean {
  if (watchId === 0) {
    return true;
  }

  try {
    geolocationProvider.clearWatch(watchId);
    return true;
  } catch {
    return false;
  }
}

/**
 * Removes a watch from the teardown registry after `clearWatch` is called.
 */
export function unregisterGeolocationWatch(
  geolocationProvider: Geolocation,
  watchId: number,
): void {
  if (watchId === 0) {
    return;
  }

  for (const entry of activeWatches) {
    if (
      entry.geolocationProvider === geolocationProvider &&
      entry.watchId === watchId
    ) {
      activeWatches.delete(entry);
      return;
    }
  }
}

/**
 * Stops every registered geolocation watch. Used when the WKWebView page is unloading
 * so WebKit does not deliver position callbacks after teardown begins.
 */
export function clearAllGeolocationWatches(): void {
  for (const entry of activeWatches) {
    clearGeolocationWatch(entry.geolocationProvider, entry.watchId);
  }
  activeWatches.clear();
}
